import { type ReactNode, type FC } from 'react';
import BasicModal from '__components/Modals/presets/BasicModal';
import { type TFunction, useTranslation } from 'react-i18next';
import { StyledExecutionData } from './styles';
import type {
  ExecutionProgress,
  SmartConnectorMetaData,
} from 'pages/SmartConnectors/types';
import {
  DRY_RUN_EXECUTION_SEARCH_PARAM,
  SMARTCONNECTORS_PATHNAME,
  EXECUTION_ID_SEARCH_PARAM,
} from 'pages/SmartConnectors/constants';
import { KizenTypography } from '__app/typography';

const parseExecutionProgress = (
  progress: ExecutionProgress,
  metaData: SmartConnectorMetaData,
  isDryRun: boolean,
  t: TFunction
): ReactNode => {
  if (!progress || !Array.isArray(progress) || !progress.length || !metaData) {
    return '';
  }
  const nodes: ReactNode[] = [];
  progress.forEach((group, gi) => {
    nodes.push(
      `• ${metaData.execution_flow.group[group.type]}: ${metaData.execution_flow.step_status[group.status]}\n`
    );
    group.steps.forEach((step, i: number) => {
      const statusLabel = metaData.execution_flow.step_status[step.status];
      if (step.smart_connector) {
        nodes.push(
          `  ${i + 1}. ${t('Run')} "${step.smart_connector.name}": ${statusLabel}\n`
        );

        const childExecutionId = step.smart_connector_execution?.id;
        const url = new URL(
          `${SMARTCONNECTORS_PATHNAME}/${step.smart_connector.id}/settings/executions`,
          window.location.origin
        );
        if (childExecutionId) {
          url.searchParams.set(EXECUTION_ID_SEARCH_PARAM, childExecutionId);
        }
        if (isDryRun) {
          url.searchParams.set(DRY_RUN_EXECUTION_SEARCH_PARAM, 'true');
        }

        nodes.push(
          `     ${t('Execution')}`,
          childExecutionId
            ? step.outcome
              ? ` (${metaData.execution_flow.step_outcome?.[step.outcome] ?? step.outcome})`
              : ''
            : ` (${t('None')})`,
          ' - ',
          <KizenTypography
            type="anchor"
            key={`exec-${gi}-${i}`}
            href={url.toString()}
            target="_blank"
            rel="noopener noreferrer"
          >
            {childExecutionId ? t('View') : t('View All')}
          </KizenTypography>,
          '\n'
        );
      } else if (step.scope) {
        nodes.push(
          `  ${i + 1}. ${[step.scope, step.custom_object?.object_name].filter(Boolean).join(' - ')}: ${step.custom_object ? `${step.invalid_records + step.valid_records} / ${step.total} ` : statusLabel}\n`
        );
      }
    });
  });
  return nodes;
};

export const ExecutionProgressModal: FC<{
  onConfirm: () => void;
  onHide: () => void;
  show: boolean;
  stepProgress: ExecutionProgress;
  metaData: SmartConnectorMetaData;
  isDryRun: boolean;
}> = ({ onConfirm, onHide, show, stepProgress, metaData, isDryRun }) => {
  const { t } = useTranslation();

  return (
    <BasicModal
      onConfirm={onConfirm}
      onHide={onHide}
      show={show}
      heading={t('Execution Progress')}
      leftBtn={null}
      buttonText={t('Close')}
      actionBtnColor="blue"
      size="medium"
    >
      <StyledExecutionData>
        {parseExecutionProgress(stepProgress, metaData, isDryRun, t)}
      </StyledExecutionData>
    </BasicModal>
  );
};
